import { DAEMON_URL } from "../config";
import type { LedgerFinding } from "./codeReview";

/** Money fields are absent for members: the daemon drops them at every depth. */
export interface BobRun {
  id: number;
  repo: string;
  branch: string;
  kind: "push" | "seed" | "manual" | string;
  base_sha: string | null;
  head_sha: string;
  created_at: string;
  found: number;
  closed: number;
  reopened: number;
  deferred: number;
  checked?: number;
  reconfirmed?: number;
  changed_files: number | null;
  open_at_start: number | null;
  error: string | null;
  displaced_by_seed: number | null;
  new_count: number;
  fixed_count: number;
  reopened_count: number;
  cost_usd?: number;
}

export interface BobRunsResponse {
  ok: true;
  repo: string;
  branch: string;
  kind: string | null;
  items: BobRun[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface BobRunReport {
  ok: true;
  run: BobRun;
  findings: {
    new: LedgerFinding[];
    new_total: number;
    fixed: LedgerFinding[];
    fixed_total: number;
    /** Of this run's new findings, how many are open now. */
    still_open_count: number;
    reopened_count: number;
  };
  same_head_run_ids: number[];
  changed_files_count: number | null;
}

export interface PostureWeek {
  /** Monday, YYYY-MM-DD, UTC. */
  week_start: string;
  opened: number;
  fixed: number;
  open_at_end: number;
  runs: number;
  spend_usd?: number;
}

export interface BobPosture {
  open: Record<string, number>;
  open_total: number;
  open_by_category: Array<{ category: string; open: number }>;
  hottest_files: Array<{ repo: string; file: string; open: number }>;
  fixed_last_7d: number;
  fixed_in_window: number;
  fix_rate: number | null;
  weekly: PostureWeek[];
  runs_by_kind: Record<string, number>;
  last_completed_run: BobRun | null;
  window_start: string;
  spend_total_usd?: number;
}

export interface PackPosture extends BobPosture {
  repo: string;
  branch: string;
  /** Set by the daemon on the all-packs answer: the first project the repo belongs to. */
  project?: string;
  project_name?: string;
  /** Every project the caller can open on this repo; two projects can share one. */
  projects?: Array<{ id: string; name: string }>;
}

export interface BobStatsResponse extends BobPosture {
  ok: true;
  repo: string;
  branch: string | null;
  days: number;
  generated_at: string;
  /** All packs only. "all" is Bob's own aggregate; "accessible" was rebuilt from the caller's packs. */
  scope?: "all" | "accessible";
  per_repo?: PackPosture[];
}

export const STATS_RANGES = [30, 90, 180] as const;
export type StatsRange = (typeof STATS_RANGES)[number];

export class BobReportError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${DAEMON_URL}${path}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  let body: unknown = null;
  try { body = await res.json(); } catch { /* keep null */ }
  if (!res.ok) {
    const j = (body ?? {}) as { error?: unknown; configured?: unknown };
    const msg = typeof j.error === "string" ? j.error : j.configured === false ? "Code review is not connected" : `http ${res.status}`;
    throw new BobReportError(msg, res.status);
  }
  return body as T;
}

const projectPath = (project: string) => `/projects/${encodeURIComponent(project)}/code-review`;

export function listRuns(
  project: string,
  opts: { kind?: "push" | "seed" | null; limit?: number; offset?: number } = {},
): Promise<BobRunsResponse> {
  const qs = new URLSearchParams();
  if (opts.kind) qs.set("kind", opts.kind);
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  if (opts.offset !== undefined) qs.set("offset", String(opts.offset));
  const q = qs.toString();
  return getJson(`${projectPath(project)}/runs${q ? `?${q}` : ""}`);
}

export function getRun(project: string, id: number): Promise<BobRunReport> {
  return getJson(`${projectPath(project)}/runs/${id}`);
}

export function packStats(project: string, days: StatsRange): Promise<BobStatsResponse> {
  return getJson(`${projectPath(project)}/stats?days=${days}`);
}

export function allPacksStats(days: StatsRange): Promise<BobStatsResponse> {
  return getJson(`/code-review/stats?days=${days}`);
}
